<script setup lang="ts">
import LeTooltip from "@dsk/components/LeTooltip.vue";
import { computed } from "vue";

import { DictsysArticleGenderHelper } from "@/helpers";
import type { DictsysArticle, DictsysGender } from "@/types";

const props = defineProps<{
  genders: DictsysGender[];
  displayLanguage: string;
  usageLanguage: DictsysArticle["usageLanguage"];
}>();

const genders = computed<DictsysArticleGenderHelper.ArticleGenders>(() => {
  const isNordic = ["da", "sv"].includes(props.displayLanguage);
  return new DictsysArticleGenderHelper.ArticleGenders(
    props.genders,
    isNordic ? DictsysArticleGenderHelper.NORDIC_GENDER_MAP : DictsysArticleGenderHelper.LATIN_GENDER_MAP
  );
});

const shortGenders = computed<string[]>(() => genders.value.getShort());

// First gender should be sufficient for the screen reader, to avoid information overload
// Usually latin, except in deen.
const and = computed<string>(() => (props.usageLanguage === "de" ? "und" : "og"));
const screenReaderText = computed<string>(() =>
  genders.value
    .getFull()
    .map((gender) => gender[0])
    .join(` ${and.value} `)
);
</script>

<template>
  <span v-if="shortGenders.length">
    <LeTooltip :margin-bottom="8" class="le-article-genders" aria-hidden="true">
      <template #content>
        <p v-for="(gender, index) in genders.getFull()" :key="index" class="le-article-genders__tooltip-text">
          {{ gender.join(" / ") }}
        </p>
      </template>

      {{ shortGenders.join("/") }}
    </LeTooltip>

    <div class="sr-only">{{ screenReaderText }}</div>
  </span>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-genders {
  color: @colors[@neutrals][300];
  .dictsys-article-text();

  &__tooltip-text {
    margin: 0;
  }
}
</style>
